/**
 * Admin Support API Contract
 *
 * Defines the oRPC contract for god-mode admin support endpoints —
 * company search, user management, bulk operations, and support tooling.
 */
import { oc } from "@orpc/contract";
import { AdminSearchCompaniesInputSchema, AdminSearchCompaniesResponseSchema, AdminListCompanyUsersInputSchema, AdminListCompanyUsersResponseSchema, AdminListCompanyInvitationsInputSchema, AdminListCompanyInvitationsResponseSchema, AdminGetCompanyLocationsInputSchema, AdminGetCompanyLocationsResponseSchema, AdminGetCompanyJobRoleFamiliesInputSchema, AdminGetCompanyJobRoleFamiliesResponseSchema, AdminSendWelcomeEmailInputSchema, AdminResetInvitationInputSchema, AdminTriggerPasswordResetInputSchema, AdminOnboardUserInputSchema, AdminOnboardUserResponseSchema, AdminDeactivateUserInputSchema, AdminReactivateUserInputSchema, AdminBulkCreateShiftsInputSchema, AdminBulkCreateShiftsResponseSchema, AdminBulkCreateTimecardsInputSchema, AdminBulkCreateTimecardsResponseSchema, AdminSuccessResponseSchema, } from "@hourly/api/adminSupport";
// ============ Contract Definitions ============
const searchCompanies = oc
    .route({
    method: "GET",
    path: "/admin/companies",
    summary: "Search companies by name",
    tags: ["Admin Support"],
})
    .input(AdminSearchCompaniesInputSchema)
    .output(AdminSearchCompaniesResponseSchema);
const listCompanyUsers = oc
    .route({
    method: "GET",
    path: "/admin/users",
    summary: "List users for a company",
    tags: ["Admin Support"],
})
    .input(AdminListCompanyUsersInputSchema)
    .output(AdminListCompanyUsersResponseSchema);
const listCompanyInvitations = oc
    .route({
    method: "GET",
    path: "/admin/invitations",
    summary: "List invitations for a company",
    tags: ["Admin Support"],
})
    .input(AdminListCompanyInvitationsInputSchema)
    .output(AdminListCompanyInvitationsResponseSchema);
const getCompanyLocations = oc
    .route({
    method: "GET",
    path: "/admin/locations",
    summary: "Get locations for a company",
    tags: ["Admin Support"],
})
    .input(AdminGetCompanyLocationsInputSchema)
    .output(AdminGetCompanyLocationsResponseSchema);
const getCompanyJobRoleFamilies = oc
    .route({
    method: "GET",
    path: "/admin/job-role-families",
    summary: "Get job role families for a company",
    tags: ["Admin Support"],
})
    .input(AdminGetCompanyJobRoleFamiliesInputSchema)
    .output(AdminGetCompanyJobRoleFamiliesResponseSchema);
const sendWelcomeEmail = oc
    .route({
    method: "POST",
    path: "/admin/send-welcome-email",
    summary: "Send welcome email to a user",
    tags: ["Admin Support"],
})
    .input(AdminSendWelcomeEmailInputSchema)
    .output(AdminSuccessResponseSchema);
const resetInvitation = oc
    .route({
    method: "POST",
    path: "/admin/reset-invitation",
    summary: "Reset an expired invitation",
    tags: ["Admin Support"],
})
    .input(AdminResetInvitationInputSchema)
    .output(AdminSuccessResponseSchema);
const triggerPasswordReset = oc
    .route({
    method: "POST",
    path: "/admin/trigger-password-reset",
    summary: "Trigger password reset for a user",
    tags: ["Admin Support"],
})
    .input(AdminTriggerPasswordResetInputSchema)
    .output(AdminSuccessResponseSchema);
const onboardUser = oc
    .route({
    method: "POST",
    path: "/admin/onboard-user",
    summary: "Onboard a new user to a company",
    tags: ["Admin Support"],
    successStatus: 201,
})
    .input(AdminOnboardUserInputSchema)
    .output(AdminOnboardUserResponseSchema);
const deactivateUser = oc
    .route({
    method: "POST",
    path: "/admin/deactivate-user",
    summary: "Deactivate a user employment",
    tags: ["Admin Support"],
})
    .input(AdminDeactivateUserInputSchema)
    .output(AdminSuccessResponseSchema);
const reactivateUser = oc
    .route({
    method: "POST",
    path: "/admin/reactivate-user",
    summary: "Reactivate a user employment",
    tags: ["Admin Support"],
})
    .input(AdminReactivateUserInputSchema)
    .output(AdminSuccessResponseSchema);
const bulkCreateShifts = oc
    .route({
    method: "POST",
    path: "/admin/bulk-create-shifts",
    summary: "Bulk create shifts for a company",
    tags: ["Admin Support"],
    successStatus: 201,
})
    .input(AdminBulkCreateShiftsInputSchema)
    .output(AdminBulkCreateShiftsResponseSchema);
const bulkCreateTimecards = oc
    .route({
    method: "POST",
    path: "/admin/bulk-create-timecards",
    summary: "Bulk create timecards for a company",
    tags: ["Admin Support"],
    successStatus: 201,
})
    .input(AdminBulkCreateTimecardsInputSchema)
    .output(AdminBulkCreateTimecardsResponseSchema);
// ============ Export Contract ============
export const adminSupportContract = {
    searchCompanies,
    listCompanyUsers,
    listCompanyInvitations,
    getCompanyLocations,
    getCompanyJobRoleFamilies,
    sendWelcomeEmail,
    resetInvitation,
    triggerPasswordReset,
    onboardUser,
    deactivateUser,
    reactivateUser,
    bulkCreateShifts,
    bulkCreateTimecards,
};
//# sourceMappingURL=adminSupport.js.map